import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponse } from '@card-software/library';
import { environment } from '../../environments/environment';
import {
  ApAdvancePayment,
  ApAdvancePaymentInsert,
  ApAdvancePaymentPostConfirm,
  ApAdvancePaymentSearchCommand,
} from '../models/ap-advance/ap-advance';
import { PagedResult } from './bank-account.proxy.service';

@Injectable({ providedIn: 'root' })
export class ApAdvanceProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.financeApi + '/api/v1/ap/advance-payments';

  public getById(id: string): Observable<ApiResponse<ApAdvancePayment>> {
    return this.httpClient.get<ApiResponse<ApAdvancePayment>>(`${this.baseUrl}/${id}`);
  }

  /** Posted advances (Anticipos) earmarked to any of the given Purchase Orders (#4516) — the "advances per PO"
   * context shown in a document's resumen. One batched call, no N+1. Empty PO list ⇒ no request. */
  public getPostedByPurchaseOrders(purchaseOrderReferences: string[]): Observable<ApiResponse<ApAdvancePayment[]>> {
    let params = new HttpParams();
    for (const ref of purchaseOrderReferences) params = params.append('purchaseOrderReferences', ref);
    return this.httpClient.get<ApiResponse<ApAdvancePayment[]>>(`${this.baseUrl}/by-purchase-orders`, { params });
  }

  public createDraft(data: ApAdvancePaymentInsert): Observable<ApiResponse<ApAdvancePayment>> {
    return this.httpClient.post<ApiResponse<ApAdvancePayment>>(this.baseUrl, data);
  }

  public updateDraft(id: string, data: ApAdvancePaymentInsert): Observable<ApiResponse<ApAdvancePayment>> {
    return this.httpClient.put<ApiResponse<ApAdvancePayment>>(`${this.baseUrl}/${id}`, data);
  }

  public post(id: string, confirm: ApAdvancePaymentPostConfirm): Observable<ApiResponse<ApAdvancePayment>> {
    return this.httpClient.post<ApiResponse<ApAdvancePayment>>(`${this.baseUrl}/${id}/post`, confirm);
  }

  public uploadReceipt(id: string, file: File): Observable<ApiResponse<ApAdvancePayment>> {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.httpClient.post<ApiResponse<ApAdvancePayment>>(`${this.baseUrl}/${id}/receipt`, form);
  }

  public search(
    command: ApAdvancePaymentSearchCommand,
    pageNumber = 1,
    pageSize = 25,
    sortField?: string,
    sortOrder = -1,
  ): Observable<ApiResponse<PagedResult<ApAdvancePayment>>> {
    let params = new HttpParams().set('pageNumber', pageNumber).set('pageSize', pageSize).set('sortOrder', sortOrder);
    if (sortField) params = params.set('sortField', sortField);
    return this.httpClient.post<ApiResponse<PagedResult<ApAdvancePayment>>>(`${this.baseUrl}/search`, command, {
      params,
    });
  }

  /**
   * Deletes a DRAFT document (#5223). The API refuses anything further along, so this is safe to offer
   * from a draft screen and safe to have refused if the status moved on in another tab.
   */
  public delete(id: string): Observable<ApiResponse<string>> {
    return this.httpClient.delete<ApiResponse<string>>(`${this.baseUrl}/${id}`);
  }
}
